// =============================================================================
// 严格基于 docs/ui_sidebar/templates/01-Layout.tsx.template,
// 仅替换 CUSTOMIZE 部分为 gitman 的功能(Logo / 关于系统 / 用户/登出)。
// 用户菜单在顶栏右侧(方案 B)；侧栏滚动条按 docs/ui_scroller 规范处理。
// =============================================================================

import { Fragment, useState, useRef, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  NavItem,
  AppItem,
  NavSectionHeader,
} from '@fluentui/react-nav'
import {
  tokens,
  makeStyles,
  mergeClasses,
  Avatar,
  Menu,
  MenuTrigger,
  MenuButton,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  SearchBox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Text,
} from '@fluentui/react-components'
import { SettingsRegular, SignOut20Regular } from '@fluentui/react-icons'

import { useAuthStore } from '../store/auth'
import { ROUTES } from '../routes'
import { NAV, NAV_TOP } from '../nav/schema'
import { useNavVisibility } from '../nav/useNavVisibility'
import { PageBreadcrumb } from './PageBreadcrumb'
import { UserSettingsDialog } from './UserSettingsDialog'

// gitman 关于信息(侧栏 Logo 点击弹出的「关于系统」内容)
const SYSTEM_ABOUT = {
  name: 'Ming-GIT',
  version: '0.2.0',
  description:
    'Ming-GIT 是面向团队的 Git 代码托管与协作平台，提供仓库管理、用户与权限、备份与恢复、OAuth 登录等能力。',
  copyright: '© 2026 Ming. 保留所有权利。',
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
  },
  navDrawer: {
    height: '100%',
    flexShrink: 0,
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  breadcrumbBar: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    minHeight: '40px',
    padding: '0 20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  contentBody: {
    flex: 1,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: '20px',
  },
  // 顶栏右侧用户区(方案 B:用户菜单在顶栏,侧栏无 Footer)
  topbarUser: {
    flexShrink: 0,
    marginLeft: 'auto',
    paddingLeft: '12px',
  },
  userButton: {
    justifyContent: 'flex-start',
    maxWidth: '200px',
  },
  navSearch: {
    padding: '8px 12px',
  },
  // 侧栏滚动容器：竖向滚动条默认隐藏，滚动中 / 悬浮容器时显示（docs/ui_scroller §5）
  navBody: {
    // WebKit/Chromium
    '::-webkit-scrollbar': { width: '8px', height: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
    ':hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    ':hover::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.04)' },
    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    ':hover': { scrollbarColor: 'rgba(0,0,0,0.25) transparent' },
  },
  // 滚动进行中：显示滚动条（由 scroll 事件 + debounce 控制）
  navBodyScrolling: {
    '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    scrollbarColor: 'rgba(0,0,0,0.25) transparent',
  },
  // 「关于系统」Dialog 外观(复用 Alert Dialog：点遮罩不关闭)
  aboutSurface: {
    width: '480px',
    maxWidth: '90vw',
  },
  aboutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  aboutLogo: {
    width: '32px',
    height: '32px',
    flexShrink: 0,
  },
  aboutName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
  },
  aboutVersion: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  aboutDesc: {
    display: 'block',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '20px',
  },
  aboutCopyright: {
    display: 'block',
    marginTop: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
})

export function Layout() {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [query, setQuery] = useState('')
  const [aboutOpen, setAboutOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // ── 侧栏滚动条显隐：滚动进行中短暂显示(debounce 400ms 后隐藏)；悬浮容器时由 CSS :hover 显示 ──
  const [navScrolling, setNavScrolling] = useState(false)
  const navScrollTimer = useRef<number | undefined>(undefined)
  const handleNavScroll = () => {
    setNavScrolling(true)
    if (navScrollTimer.current !== undefined) window.clearTimeout(navScrollTimer.current)
    navScrollTimer.current = window.setTimeout(() => setNavScrolling(false), 400)
  }
  // 卸载时清理滚动 debounce 定时器
  useEffect(() => () => {
    if (navScrollTimer.current !== undefined) window.clearTimeout(navScrollTimer.current)
  }, [])

  const handleSignOut = () => {
    logout()
    navigate(ROUTES.login, { replace: true })
  }

  // 1) 权限过滤：基于 permissions(由 is_admin 派生)
  const visibleGroups = useNavVisibility(NAV)

  // 2) 搜索过滤：label / path / description 模糊匹配
  const q = query.trim().toLowerCase()
  const topMatch =
    !q ||
    NAV_TOP.label.toLowerCase().includes(q) ||
    NAV_TOP.path.toLowerCase().includes(q) ||
    (NAV_TOP.description ?? '').toLowerCase().includes(q)

  const filteredGroups = visibleGroups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) =>
          !q ||
          i.label.toLowerCase().includes(q) ||
          i.path.toLowerCase().includes(q) ||
          (i.description ?? '').toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.items.length > 0)

  // 3) 当前高亮：最长前缀匹配
  const allNavPaths = [
    NAV_TOP.path,
    ...visibleGroups.flatMap((g) => g.items.map((i) => i.path)),
  ]
  const activeValue =
    allNavPaths
      .filter((p) => location.pathname === p || location.pathname.startsWith(p + '/'))
      .sort((a, b) => b.length - a.length)[0] ?? NAV_TOP.path

  return (
    <div className={styles.root}>
      <NavDrawer
        type="inline"
        open
        defaultSelectedValue={activeValue}
        className={styles.navDrawer}
        onNavItemSelect={(_, data) => {
          const v = data.value as string
          navigate(v)
        }}
      >
        {/* === Brand Bar === */}
        <NavDrawerHeader>
          <AppItem
            icon={<img src="/icons/logo-32.png" alt="logo" width={24} height={24} />}
            aria-label={`关于系统 ${SYSTEM_ABOUT.name}`}
            onClick={() => setAboutOpen(true)}
          >
            {SYSTEM_ABOUT.name}
          </AppItem>
          <div className={styles.navSearch}>
            <SearchBox
              placeholder="搜索菜单"
              value={query}
              onChange={(_, data) => setQuery(data.value)}
              size="small"
            />
          </div>
        </NavDrawerHeader>

        {/* === Menu 区 === */}
        <NavDrawerBody
          className={mergeClasses(styles.navBody, navScrolling && styles.navBodyScrolling)}
          onScroll={handleNavScroll}
        >
          {topMatch && (
            <NavItem value={NAV_TOP.path} icon={NAV_TOP.icon}>
              {NAV_TOP.label}
            </NavItem>
          )}
          {filteredGroups.map((group) => (
            <Fragment key={group.section}>
              <NavSectionHeader>{group.section}</NavSectionHeader>
              {group.items.map((n) => (
                <NavItem key={n.path} value={n.path} icon={n.icon}>
                  {n.label}
                </NavItem>
              ))}
            </Fragment>
          ))}
        </NavDrawerBody>
      </NavDrawer>

      <main className={styles.main}>
        <div className={styles.breadcrumbBar}>
          <PageBreadcrumb />
          {/* 顶栏右侧用户区(方案 B:用户菜单在顶栏,侧栏无 Footer) */}
          <div className={styles.topbarUser}>
            {user && (
              <Menu>
                <MenuTrigger>
                  <MenuButton appearance="subtle" size="small" className={styles.userButton}>
                    <Avatar
                      name={user.display_name ?? user.username}
                      size={24}
                      image={user.avatar_url ? { src: user.avatar_url } : undefined}
                    />
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '13px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user.username}
                    </span>
                  </MenuButton>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<SettingsRegular />}
                      onClick={() => setSettingsOpen(true)}
                    >
                      设置
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem icon={<SignOut20Regular />} onClick={handleSignOut}>
                      退出登录
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
          </div>
        </div>
        <div className={styles.contentBody}>
          <Outlet />
        </div>
      </main>

      {/* 关于系统：侧栏 LOGO 点击触发(Alert Dialog，点遮罩不关闭) */}
      <Dialog open={aboutOpen} onOpenChange={(_, d) => setAboutOpen(d.open)} modalType="alert">
        <DialogSurface className={styles.aboutSurface}>
          <DialogBody>
            <DialogTitle>关于 {SYSTEM_ABOUT.name}</DialogTitle>
            <DialogContent>
              <div className={styles.aboutHeader}>
                <img src="/icons/logo-32.png" alt="logo" className={styles.aboutLogo} />
                <div>
                  <div className={styles.aboutName}>{SYSTEM_ABOUT.name}</div>
                  <div className={styles.aboutVersion}>版本 {SYSTEM_ABOUT.version}</div>
                </div>
              </div>
              <Text className={styles.aboutDesc}>{SYSTEM_ABOUT.description}</Text>
              <Divider />
              <Text className={styles.aboutCopyright}>{SYSTEM_ABOUT.copyright}</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setAboutOpen(false)}>
                知道了
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 用户设置：右上角用户菜单 → 设置(改密码) */}
      <UserSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  )
}